import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Select,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  TextField,
  Tooltip,
  Chip,
  Checkbox,
  ListItemText
} from '@mui/material';
import { Warning, Business, ClearAll } from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

// Vendor interface
interface Vendor {
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  EmailID: string;
  CreatedDate: string;
  Business: string;
  Address: string;
  SubscribedCategories: {
    CategoryID: number;
    CategoryName: string;
  }[];
}

// API Response interface
interface VendorAPIResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    TotalPages: number;
    Data: Vendor[];
  };
}

// Search Criteria interface
interface VendorSearchCriteria {
  firstName: string;
  email: string;
  business: string;
  category: number[];
}

interface CategoryOption {
  CategoryID: number;
  CategoryName: string;
}

const AllRegisteredVendors: React.FC = () => {
  const theme = useTheme();

  const [searchCriteria, setSearchCriteria] = useState<VendorSearchCriteria>({
    firstName: '',
    email: '',
    business: '',
    category: []
  });

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // State for dropdown data
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);

  // Fetch categories for dropdown
  useEffect(() => {
    const fetchCategories = async () => {
      setLoadingDropdowns(true);
      setDropdownError(null);
      try {
        const categoriesResponse = await httpRequest(
          'GET',
          `/bid_portal/category/get_all`,
          {},
          {}
        );

        if (categoriesResponse.status === 'success') {
          const categoriesData =
            categoriesResponse.data?.data ||
            categoriesResponse.data?.Data ||
            [];
          setCategories(categoriesData);
        } else {
          console.error('Failed to fetch categories:', categoriesResponse);
          setDropdownError('Failed to load categories. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        setDropdownError('Failed to load categories. Please try again.');
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchCategories();
  }, []);

  const fetchVendors = async (
    pageNum: number,
    pageSize: number,
    criteria?: VendorSearchCriteria
  ) => {
    setLoading(true);
    try {
      // Use provided criteria or fall back to current state
      const activeCriteria = criteria || searchCriteria;
      // Construct query parameters
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(),
        Size: pageSize.toString(),
        ...(activeCriteria.firstName.trim() && {
          Name: encodeURIComponent(activeCriteria.firstName.trim())
        }),
        ...(activeCriteria.email.trim() && {
          Name: encodeURIComponent(activeCriteria.email.trim())
        }),
        ...(activeCriteria.business.trim() && {
          Business: encodeURIComponent(activeCriteria.business.trim())
        }),
        ...(activeCriteria.category.length > 0 && {
          Category: activeCriteria.category.join(',')
        })
      });

      const response = (await httpRequest(
        'GET',
        `/auth/vendors?${queryParams}`
      )) as VendorAPIResponse;

      if (response.status === 'success') {
        setVendors(response.data.Data);
        setTotalCount(response.data.TotalRecords);
      } else {
        toast.error(response.message || 'Failed to fetch vendors');
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to fetch vendors. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchVendors(0, rowsPerPage);
  };

  const handleClear = () => {
    const clearedCriteria: VendorSearchCriteria = {
      firstName: '',
      email: '',
      business: '',
      category: []
    };

    setSearchCriteria(clearedCriteria);
    setPage(0);
    fetchVendors(0, rowsPerPage, clearedCriteria); // Pass cleared criteria directly
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to format date and time
  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  // Format subscribed categories
  const formatCategories = (
    subscribedCategories: { CategoryID: number; CategoryName: string }[]
  ) => {
    if (!subscribedCategories || subscribedCategories.length === 0) return '-';
    return subscribedCategories.map((cat) => cat.CategoryName).join(', ');
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 170,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          All Registered Vendors
        </Typography>
      </Box>

      {dropdownError && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          {dropdownError}
        </Alert>
      )}

      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {loadingDropdowns ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {/* First row - Name/Email and Category */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Name / Email"
                  variant="outlined"
                  value={searchCriteria.firstName || searchCriteria.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    // If the value contains @, treat it as email, otherwise as firstName
                    if (value.includes('@')) {
                      setSearchCriteria({
                        ...searchCriteria,
                        email: value,
                        firstName: ''
                      });
                    } else {
                      setSearchCriteria({
                        ...searchCriteria,
                        firstName: value,
                        email: ''
                      });
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      height: 40
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      transform: 'translate(14px, 11px) scale(1)',
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '&.Mui-focused': {
                        fontWeight: 500
                      },
                      backgroundColor: 'white',
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      zIndex: 1
                    }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    open={openCategorySelect}
                    onOpen={() => setOpenCategorySelect(true)}
                    onClose={() => setOpenCategorySelect(false)}
                    multiple
                    value={searchCriteria.category}
                    onChange={(e) => {
                      const selectedValues = e.target.value as any[];

                      // Handle "Select All" logic
                      if (selectedValues.includes('SELECT_ALL')) {
                        const allCategoryIDs = categories?.map(
                          (cat) => cat.CategoryID
                        );
                        const isAllSelected =
                          searchCriteria.category?.length ===
                          categories?.length;

                        // If all categories are selected, deselect all; otherwise, select all
                        const finalCategories = isAllSelected
                          ? []
                          : allCategoryIDs;

                        setSearchCriteria({
                          ...searchCriteria,
                          category: finalCategories
                        });
                      } else {
                        // Ensure no invalid "Unknown" category is included
                        const validSelectedValues = selectedValues.filter(
                          (value) =>
                            categories.some((cat) => cat.CategoryID === value)
                        );

                        setSearchCriteria({
                          ...searchCriteria,
                          category: validSelectedValues
                        });
                      }
                    }}
                    sx={{
                      borderRadius: 1.5,
                      height: 40,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(theme.palette.primary.main, 0.5)
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2
                      },
                      '& .MuiSelect-select': {
                        paddingRight: '32px !important'
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedArray = selected as number[];
                      const totalSelected = selectedArray?.length;

                      if (totalSelected === 0) {
                        return (
                          <Box sx={{ color: 'text.secondary' }}>
                            Select categories
                          </Box>
                        );
                      }

                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            width: '100%',
                            overflow: 'hidden',
                            position: 'relative'
                          }}
                        >
                          {/* Scrollable chips container */}
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.5,
                              overflow: 'hidden',
                              flexGrow: 1,
                              maskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none',
                              WebkitMaskImage:
                                totalSelected > 1
                                  ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                  : 'none'
                            }}
                          >
                            {selectedArray?.map((CategoryID) => {
                              const categoryName =
                                categories.find(
                                  (c) => c.CategoryID === CategoryID
                                )?.CategoryName || 'Unknown';
                              return (
                                <Chip
                                  key={CategoryID}
                                  label={categoryName}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 500,
                                    borderRadius: 1,
                                    flexShrink: 0,
                                    maxWidth: '120px',
                                    '& .MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      paddingLeft: '8px',
                                      paddingRight: '8px'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Box>

                          {/* Count badge - always visible on the right */}
                          {totalSelected > 3 && (
                            <Chip
                              label={`${totalSelected}`}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.75rem',
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                color: theme.palette.primary.main,
                                fontWeight: 600,
                                borderRadius: 1,
                                minWidth: '40px',
                                flexShrink: 0,
                                position: 'absolute',
                                right: 0,
                                backgroundColor: 'white',
                                boxShadow: '-4px 0 8px rgba(255, 255, 255, 0.9)'
                              }}
                            />
                          )}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 350,
                          borderRadius: 1.5,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          marginTop: 1
                        }
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left'
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left'
                      }
                    }}
                  >
                    {/* Select All Option */}
                    <MenuItem value="SELECT_ALL">
                      <Checkbox
                        checked={
                          searchCriteria.category?.length === categories?.length
                        }
                        indeterminate={
                          searchCriteria.category?.length > 0 &&
                          searchCriteria.category?.length < categories?.length
                        }
                      />
                      <ListItemText
                        primary="Select All"
                        sx={{ fontWeight: 'bold', color: 'primary.main' }}
                      />
                    </MenuItem>

                    {/* Individual Category Options */}
                    {categories?.map((category) => (
                      <MenuItem
                        key={category.CategoryID}
                        value={category.CategoryID}
                        sx={{
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.08
                            ),
                            '&:hover': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.12
                              )
                            }
                          }
                        }}
                      >
                        <Checkbox
                          checked={
                            searchCriteria.category.indexOf(
                              category.CategoryID
                            ) > -1
                          }
                        />
                        {category.CategoryName}
                      </MenuItem>
                    ))}

                    <MenuItem
                      disableRipple
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: '1px solid #ddd',
                        mt: 1,
                        pt: 1
                      }}
                    >
                      <button
                        style={{
                          backgroundColor: '#1976d2',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '6px',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                        onClick={() => setOpenCategorySelect(false)}
                      >
                        Apply
                      </button>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Second row - Business */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Business"
                  variant="outlined"
                  value={searchCriteria.business}
                  onChange={(e) =>
                    setSearchCriteria({
                      ...searchCriteria,
                      business: e.target.value
                    })
                  }
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      height: 40
                    }
                  }}
                />
              </Grid>

              {/* Search and Clear buttons */}
              <Grid item xs={12} sm={12} justifyItems={'flex-end'}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    justifyContent: 'flex-start',
                    alignItems: 'flex-end',
                    height: '100%'
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      px: 3,
                      py: 0.8,
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleClear}
                    startIcon={<ClearAll />}
                    size="small"
                    sx={{
                      borderRadius: 1.5,
                      px: 3,
                      py: 0.8,
                      fontWeight: 'bold',
                      borderWidth: '1px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderWidth: '1px',
                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        <Box
          sx={{
            p: 2.5,
            bgcolor: `linear-gradient(to right, ${alpha(
              theme.palette.primary.main,
              0.05
            )}, ${alpha(theme.palette.primary.main, 0.02)})`,
            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
            Registered Vendors
          </Typography>
          {!loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              Total Results: {totalCount}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    py: 2,
                    fontSize: '0.875rem'
                  },
                  '& .MuiTableCell-body': {
                    py: 1.5,
                    fontSize: '0.875rem'
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email/Fax</TableCell>
                    <TableCell>Business</TableCell>
                    <TableCell>Registration Date</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>Subscribed Category</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vendors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            No vendors found. Try adjusting your search
                            criteria.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    vendors.map((vendor, index) => (
                      <TableRow
                        key={`${vendor.EmailID}-${index}`}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.03)
                          },
                          transition: 'background-color 0.2s ease',
                          '&:nth-of-type(even)': {
                            bgcolor: alpha(
                              theme.palette.background.default,
                              0.4
                            )
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {vendor.FirstName || '-'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {vendor.LastName || '-'}
                        </TableCell>
                        <TableCell>{vendor.PhoneNumber || '-'}</TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Tooltip title={vendor.EmailID} arrow>
                            {vendor.EmailID || ('-' as any)}
                          </Tooltip>
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Tooltip title={vendor.Business} arrow>
                            {vendor.Business || ('-' as any)}
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {formatDateTime(vendor.CreatedDate)}
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          <Tooltip title={vendor.Address} arrow>
                            {vendor.Address || ('-' as any)}
                          </Tooltip>
                        </TableCell>

                        <Tooltip
                          title={formatCategories(vendor.SubscribedCategories)}
                          arrow
                        >
                          <TableCell
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {formatCategories(vendor.SubscribedCategories) ||
                              ('-' as any)}
                          </TableCell>
                        </Tooltip>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                borderTop: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      fontWeight: 500
                    },
                  '& .MuiTablePagination-select': {
                    borderRadius: 1.5,
                    '&:focus': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AllRegisteredVendors;
